import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { gql, useMutation } from 'urql'
import { AlertComment } from '../../../schema'
import { UserAvatar } from '../../util/avatars'
import Markdown from '../../util/Markdown'
import { useSessionInfo } from '../../util/RequireConfig'
import { Time } from '../../util/Time'

interface AlertCommentListProps {
  comments: AlertComment[]
}

const updateMutation = gql`
  mutation UpdateAlertComment($input: UpdateAlertCommentInput!) {
    updateAlertComment(input: $input) {
      id
    }
  }
`

const deleteMutation = gql`
  mutation DeleteAlertComment($id: ID!) {
    deleteAlertComment(id: $id)
  }
`

export default function AlertCommentList({
  comments,
}: AlertCommentListProps): React.JSX.Element {
  const listRef = React.useRef<HTMLDivElement>(null)

  const { userID, isAdmin } = useSessionInfo()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedComment, setSelectedComment] = useState<AlertComment | null>(
    null,
  )

  const [editDialog, setEditDialog] = useState<AlertComment | null>(null)
  const [editMessage, setEditMessage] = useState('')
  const [editMentionMap, setEditMentionMap] = useState<Record<string, string>>(
    {},
  )

  const [deleteDialog, setDeleteDialog] = useState<AlertComment | null>(null)

  const [, updateComment] = useMutation(updateMutation)
  const [, deleteComment] = useMutation(deleteMutation)

  // Scroll to bottom when new comments are added
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [comments.length])

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    comment: AlertComment,
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedComment(comment)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedComment(null)
  }

  const handleEditClick = () => {
    if (selectedComment) {
      setEditDialog(selectedComment)

      // Parse mentions to show friendly text
      // Regex matches: [@Name](/users/ID)
      const regex = /\[(@.+?)\]\(\/users\/([0-9a-f-]+)\)/g
      const map: Record<string, string> = {}
      let friendlyMessage = selectedComment.message

      // We need to replace all occurrences and build the map
      // Using replace to iterate and substitute
      friendlyMessage = friendlyMessage.replace(
        regex,
        (fullMatch, name, id) => {
          map[name] = id
          return name // Replace with just @Name
        },
      )

      setEditMessage(friendlyMessage)
      setEditMentionMap(map)
    }
    handleMenuClose()
  }

  const handleDeleteClick = () => {
    if (selectedComment) {
      setDeleteDialog(selectedComment)
    }
    handleMenuClose()
  }

  const handleEditSubmit = () => {
    if (editDialog && editMessage.trim()) {
      let finalMessage = editMessage

      // Restore markdown links for known mentions
      Object.entries(editMentionMap).forEach(([name, id]) => {
        // Look for @Name boundaries to avoid partial matches if possible,
        // though typically @Name is distinct enough.
        // Using a similar logic to AlertCommentInput
        const regex = new RegExp(`(${name})\\b`, 'g')
        if (regex.test(finalMessage)) {
          finalMessage = finalMessage.replace(regex, `[$1](/users/${id})`)
        }
      })

      updateComment({
        input: {
          id: editDialog.id,
          message: finalMessage,
        },
      })
      setEditDialog(null)
      setEditMentionMap({})
    }
  }

  const handleDeleteSubmit = () => {
    if (deleteDialog) {
      deleteComment({ id: deleteDialog.id })
      setDeleteDialog(null)
    }
  }

  if (comments.length === 0) {
    return (
      <Typography
        variant='body2'
        color='textSecondary'
        align='center'
        sx={{ py: 2 }}
      >
        No comments yet.
      </Typography>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <List disablePadding>
        {comments.map((comment) => (
          <ListItem
            key={comment.id}
            alignItems='flex-start'
            divider
            secondaryAction={
              isAdmin || (userID && comment.author?.id === userID) ? (
                <IconButton
                  edge='end'
                  aria-label='more'
                  size='small'
                  onClick={(e) => handleMenuOpen(e, comment)}
                >
                  <MoreVertIcon fontSize='small' />
                </IconButton>
              ) : null
            }
          >
            <ListItemAvatar>
              <UserAvatar
                userID={comment.author?.id || ''}
                alt={comment.author?.name || 'System'}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <React.Fragment>
                  <Typography
                    component='span'
                    variant='subtitle2'
                    color='textPrimary'
                  >
                    {comment.author?.name || 'System'}
                  </Typography>
                  {' - '}
                  <Typography
                    component='span'
                    variant='caption'
                    color='textSecondary'
                  >
                    <Time time={comment.createdAt} />
                  </Typography>
                </React.Fragment>
              }
              secondary={
                <Box sx={{ pr: 4 }}>
                  <Typography
                    component='div'
                    variant='body2'
                    color='textPrimary'
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    <Markdown value={comment.message} />
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={Boolean(editDialog)}
        onClose={() => setEditDialog(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Edit Comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Comment'
            fullWidth
            multiline
            minRows={2}
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            color='primary'
            disabled={!editMessage.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDialog)}
        onClose={() => setDeleteDialog(null)}
      >
        <DialogTitle>Delete Comment?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this comment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
